import React from "react";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

export const useReportContext = () => {
  const [t] = useTranslation("report");
  const [info] = useTranslation("information");
  const theme = useTheme();

  // Benefits options
  const benefitOptions = [
    { label: t("Resolution.monetary"), value: "monetary" },
    { label: t("Resolution.in_kind"), value: "in_kind" },
    { label: t("Resolution.employee_benefits"), value: "employee_benefits" },
    { label: t("Resolution.congratulations"), value: "congratulations" },
  ];

  // Representation of entryway in icon and text
  const entryways = {
    WEBSITE: { iconName: "monitor", label: t("entryways.website") },
    PHONE: { iconName: "call", label: t("entryways.phone") },
    EMAIL: { iconName: "outline_sms_tracking", label: t("entryways.email") },
    CHAT: { iconName: "outline_sms", label: t("entryways.chat") },
    WHATSAPP: { iconName: "outline_whatsapp", label: t("entryways.whatsapp") },
    APP: { iconName: "mobile", label: t("entryways.app") },
    OUTDOOR: { iconName: "case_manager_icon", label: "CaseManager" },
    OTHER: { iconName: "cd_icon", label: t("entryways.other") },
  };

  // Verifying actions
  const evaluated_facts = {
    yes: {
      iconName: "bold_verify",
      color: "success",
      label: t("Resolution.case_was_verified"),
    },
    no: {
      iconName: "bold_close",
      color: "error",
      label: t("Resolution.case_was_not_verified"),
    },
    not_determined: {
      iconName: "bold_information",
      color: "secondary",
      label: t("Resolution.undetermined_case"),
    },
  };

  // facts_verified
  const facts = [
    { value: "yes", label: t("facts.yes") },
    { value: "no", label: t("facts.no") },
    { value: "not_determined", label: t("facts.not_determined") },
  ];

  // Report indicators
  const icon_indicators = {
    has_plan: {
      text: t("ReportsList.has_plan"),
      iconName: "outline_device_message",
    },
    is_locked: {
      text: t("ReportsList.is_locked"),
      iconName: "outline_lock",
    },
    has_message: {
      text: t("ReportsList.has_message"),
      iconName: "outline_directbox_receive",
    },
    is_severity: {
      text: t("ReportsList.is_severity"),
      iconName: "outline_alarm",
      iconColor: "error",
      iconColorHover: "white",
      btnColorHover: "error",
    },
    is_anonymous: {
      text: t("ReportsList.is_anonymous"),
      iconName: "outline_user_minus",
    },
    has_complement: {
      text: t("ReportsList.has_complement"),
      iconName: "outline_3d_cube_scan",
    },
    has_attachments: {
      text: t("ReportsList.has_attachments"),
      iconName: "paperclip",
    },
    has_relateds: {
      text: t("ReportsList.has_relateds"),
      iconName: "outline_hierarchy",
    },
    has_followup: {
      text: t("ReportsList.has_followup"),
      iconName: "outline_routing_2",
    },
    investigation_completed: {
      text: t("ReportsList.investigation_completed"),
      iconName: "outline_shield_tick",
    },
    reclassification: {
      text: t("ReportsList.reclassification"),
      iconName: "outline_convert",
    },
  };

  // Indicators
  const indicatorOptions = [
    {
      label: t("ReportsList.has_plan"),
      key: "indicator_has_plan",
      iconName: "outline_device_message",
    },
    {
      label: "Confirmado",
      key: "indicator_confirmed",
      iconName: "bold_verify",
      color: theme.palette.success.main,
    },
    {
      label: t("ReportsList.is_locked"),
      key: "indicator_is_locked",
      iconName: "outline_lock",
    },
    {
      label: t("ReportsList.has_message"),
      key: "indicator_has_message",
      iconName: "outline_directbox_receive",
    },
    {
      label: t("ReportsList.is_severity"),
      key: "indicator_is_severity",
      iconName: "outline_alarm",
      color: theme.palette.error.main,
    },
    {
      label: t("ReportsList.has_followup"),
      key: "indicator_has_followup",
      iconName: "outline_routing_2",
    },
    {
      label: t("ReportsList.has_relateds"),
      key: "indicator_has_relateds",
      iconName: "outline_hierarchy",
    },
    {
      label: t("ReportsList.is_anonymous"),
      key: "indicator_is_anonymous",
      iconName: "outline_user_minus",
    },
    {
      label: t("ReportsList.has_complement"),
      key: "indicator_has_complement",
      iconName: "outline_3d_cube_scan",
    },
    {
      label: t("ReportsList.has_attachments"),
      key: "indicator_has_attachments",
      iconName: "paperclip",
    },
    {
      label: t("ReportsList.investigation_completed"),
      key: "indicator_investigation_completed",
      iconName: "outline_shield_tick",
    },
    {
      label: t("ReportsList.reclassification"),
      key: "indicator_reclassification",
      iconName: "outline_convert",
    },
  ];

  const involvedColor = {
    accomplice: theme.palette.secondary.main,
    offender: theme.palette.error.main,
    witness: theme.palette.success.main,
    victim: theme.palette.blue.main,
  };

  // Offender type translate
  const involvedType = {
    offender: t("Resolution.infractor"),
    accused: t("Resolution.accused"),
    accomplice: t("Resolution.accomplice"),
    witness: t("Resolution.witness"),
    victim: t("Resolution.victim"),
  };

  // Entryways options
  const kindOfEntryways = [
    { value: "WEBSITE", label: t("entryways.website") },
    { value: "PHONE", label: t("entryways.phone") },
    { value: "EMAIL", label: t("entryways.email") },
    { value: "CHAT", label: t("entryways.chat") },
    { value: "WHATSAPP", label: t("entryways.whatsapp") },
    { value: "APP", label: t("entryways.app") },
    { value: "OUTDOOR", label: t("entryways.outdoor") },
    { value: "OTHER", label: t("entryways.other") },
  ];

  // Measures
  const measureOptions = [
    {
      label: t("Resolution.internal_process_modifications"),
      value: "internal_process_modifications",
    },
    {
      label: t("Resolution.acquisition_equipment_materials"),
      value: "acquisition_equipment_materials",
    },
    { label: t("Resolution.repairs"), value: "repairs" },
    { label: t("Resolution.hiring"), value: "hiring" },
    { label: t("Resolution.compensations"), value: "compesations" },
    {
      label: t("Resolution.dismissal_third_party_employees"),
      value: "dismissal_third_party_employees",
    },
    { label: t("Resolution.another"), value: "another" },
  ];

  // Sanctions options
  const sanctionsOptions = [
    { label: t("Resolution.verbal_warning"), value: "verbal_reprimand" },
    { label: t("Resolution.written_warning"), value: "documental_reprimand" },

    //{ label: t("Resolution.economic_sanction"), value: "economic_reprimand" },
    {
      label: t("Resolution.performance_improvement_plans"),
      value: "performance_improvement_plans",
    },

    //{ label: t("Resolution.temporary_suspension"), value: "temporary_suspension" },
    { label: t("Resolution.dismissal"), value: "dismisal" },
    {
      label: t("Resolution.commencement_legal_proceedings"),
      value: "commencement_legal_proceedings",
    },
    {
      label: t("Resolution.area_or_shift_change"),
      value: "area_or_shift_change",
    },
    { label: t("Resolution.none_apply"), value: "none_apply" },
  ];

  const severityOptions = [
    { value: "type::null", label: info("information.none") },
    { value: "type::low", label: info("information.low") },
    { value: "type::medium", label: info("information.medium") },
    { value: "type::hight", label: info("information.hight") },
    { value: "type::other", label: info("information.other") },
  ];

  // Report status (in ascending order)
  const status_locales = {
    draft: {
      label: t("status.draft"),
      color: theme.palette.secondary.main,
    },
    new: {
      label: t("status.new"),
      color: theme.palette.blue.main,
      description: t("Care.new_description"),
    },
    pending: {
      label: t("status.pending"),
      color: theme.palette.purple.main,
      description: t("Care.pending_description"),
    },
    process: {
      label: t("status.process"),
      color: theme.palette.success.main,
      description: t("Care.process_description"),
    },
    finish: {
      label: t("status.finish"),
      color: theme.palette.warning.main,
      description: t("Care.finish_description"),
    },
    closed: {
      label: t("status.closed"),
      color: theme.palette.error.main,
      description: t("Care.closed_description"),
    },
    closing_process: {
      label: t("status.closing_process"),
      color: theme.palette.orange.main,
      description: t("Care.colsing_process_description"),
    },
  };

  // Status kind
  const statusOptions = [
    { value: "draft", label: t("status.draft") },
    { value: "new", label: t("status.new") },
    { value: "pending", label: t("status.pending") },
    { value: "process", label: t("status.process") },
    { value: "finish", label: t("status.finish") },
    { value: "closed", label: t("status.closed") },
  ];

  // Dynamic titles
  const titlesType = {
    offenders: {
      title: t("Resolution.add_sanction"),
      selectLabel: t("Resolution.select_involved_party"),
      inputLabel: t("Resolution.sanctions"),
      inputPlaceholder: t("Resolution.select_one_more_sanctions"),
      addActionInput: t("Resolution.another_sanction"),
      addOtherActionPlaceholder: t("Resolution.specify_another_sanction_type"),
      addOtherActionHelperText: t("Resolution.enter_another_sanction"),
      addAnotherAction: t("Resolution.add_another_sanction"),
      deleteAction: t("Resolution.delete_sanction"),
      commentPlaceholder: t(
        "Resolution.provide_details_sanction_assigned_person_involved"
      ),
    },
    victims: {
      title: t("Resolution.add_compensation"),
      selectLabel: t("Resolution.select_beneficiary"),
      inputLabel: t("Resolution.benefits"),
      inputPlaceholder: t("Resolution.select_one_more_benefits"),
      addActionInput: t("Resolution.another_compensation"),
      addOtherActionPlaceholder: t("Resolution.specify_another_benefit_type"),
      addOtherActionHelperText: t("Resolution.enter_another_compensation"),
      addAnotherAction: t("Resolution.add_another_compensation"),
      deleteAction: t("Resolution.delete_compensation"),
      commentPlaceholder: t(
        "Resolution.provide_details_compensations_awarded_person_involved"
      ),
    },
  };

  // Monts
  const optionsMonth = [
    { value: "01", label: t("months.january") },
    { value: "02", label: t("months.february") },
    { value: "03", label: t("months.march") },
    { value: "04", label: t("months.april") },
    { value: "05", label: t("months.may") },
    { value: "06", label: t("months.june") },
    { value: "07", label: t("months.july") },
    { value: "08", label: t("months.august") },
    { value: "09", label: t("months.september") },
    { value: "10", label: t("months.october") },
    { value: "11", label: t("months.november") },
    { value: "12", label: t("months.december") },
  ];

  return {
    benefitOptions,
    entryways,
    evaluated_facts,
    facts,
    icon_indicators,
    indicatorOptions,
    involvedColor,
    involvedType,
    kindOfEntryways,
    measureOptions,
    sanctionsOptions,
    severityOptions,
    status_locales,
    statusOptions,
    titlesType,
    optionsMonth,
  };
};
